import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import LoginPage from "./pages/LoginPage";
import HomePage from "./pages/HomePage";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminCourses from "./pages/admin/AdminCourses";
import AdminTestEditor from "./pages/admin/AdminTestEditor";
import AdminProgress from "./pages/admin/AdminProgress";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminDiscountCodes from "./pages/admin/AdminDiscountCodes";
import UserDashboard from "./pages/user/UserDashboard";
import UserOrdersPage from "./pages/user/UserOrdersPage";
import TakeTest from "./pages/user/TakeTest";
import ResultPage from "./pages/user/ResultPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import PaymentCancelPage from "./pages/PaymentCancelPage";
import HelpPage from "./pages/HelpPage";
import ReaderPage from "./pages/ReaderPage";
import "./App.css";

function Protected({ children, role }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        Cargando…
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  // Post-Feb-2026 role model:
  //   - role="admin"  → any manager (gestion, admin, superadmin) may enter.
  //   - role="user"   → any alumno may enter (kept for legacy route usage).
  const MANAGER_ROLES = ["gestion", "admin", "superadmin"];
  const isManager = MANAGER_ROLES.includes(user.role);
  if (role) {
    const allowed =
      role === "admin"
        ? isManager
        : role === "user"
        ? user.role === "alumno"
        : user.role === role;
    if (!allowed) {
      return <Navigate to={isManager ? "/admin" : "/dashboard"} replace />;
    }
  }
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster richColors position="top-right" />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/payment/success" element={<PaymentSuccessPage />} />
          <Route path="/payment/cancel" element={<PaymentCancelPage />} />

          <Route
            path="/admin"
            element={
              <Protected role="admin">
                <AdminLayout />
              </Protected>
            }
          >
            <Route index element={<AdminProgress />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="courses" element={<AdminCourses />} />
            <Route path="courses/new" element={<AdminTestEditor />} />
            <Route path="courses/:id/edit" element={<AdminTestEditor />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="discount-codes" element={<AdminDiscountCodes />} />
            <Route path="help" element={<HelpPage variant="admin" />} />
          </Route>

          <Route
            path="/dashboard"
            element={
              <Protected role="user">
                <UserDashboard />
              </Protected>
            }
          />
          <Route
            path="/dashboard/orders"
            element={
              <Protected role="user">
                <UserOrdersPage />
              </Protected>
            }
          />
          <Route
            path="/dashboard/help"
            element={
              <Protected role="user">
                <HelpPage variant="user" />
              </Protected>
            }
          />
          <Route
            path="/exam/:courseId/:examId"
            element={
              <Protected>
                <TakeTest />
              </Protected>
            }
          />
          <Route
            path="/result/:id"
            element={
              <Protected>
                <ResultPage />
              </Protected>
            }
          />
          <Route
            path="/reader/exam/:courseId/:examId/:fileId"
            element={
              <Protected>
                <ReaderPage />
              </Protected>
            }
          />
          <Route
            path="/reader/course/:courseId/:fileId"
            element={
              <Protected>
                <ReaderPage />
              </Protected>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
